import { EditorState } from "@codemirror/state";
import { type EditorErrorRow, errorRows } from "./editorErrors";
import { rhaiLanguage } from "./rhaiLanguage";
import { localDiagnostics } from "./rhaiLint";
import { isUntitledScriptId } from "./stagingModel";

// The transform scripts' twin of jsonErrorRows: the offline verdict on a
// script text — syntax, scope and the transform(data, attributes) rule —
// as the editor's rows, without an editor. What the deploy gate, the stage
// tabs' alert dot and the staged rows' red icon read for every script
// draft (the engine's own verdict stays in the open editor). Cached by
// text: the same drafts are asked about on every render.

const CACHE_LIMIT = 64;
const cache = new Map<string, EditorErrorRow[]>();

/** A stored script whose draft was cleared: the engine rejects an empty
 *  script (no transform function), so it counts as broken — unlike an
 *  empty untitled draft, which is not a script yet. */
export function isBlankStoredScript(rowId: string, text: string): boolean {
  return !isUntitledScriptId(rowId) && text.trim() === "";
}

/** The error count the deploy gate and the row icon read for a draft:
 *  the offline rows, or one for a stored script cleared to nothing. */
export function scriptDraftErrorCount(rowId: string, text: string): number {
  if (isBlankStoredScript(rowId, text)) return 1;
  return scriptErrorRows(text).length;
}

export function scriptErrorRows(text: string): EditorErrorRow[] {
  if (text.trim() === "") return [];
  const hit = cache.get(text);
  if (hit) return hit;
  const state = EditorState.create({ doc: text, extensions: [rhaiLanguage] });
  const { syntax, scope } = localDiagnostics(state);
  const rows = errorRows(state, [...syntax, ...scope]);
  if (cache.size >= CACHE_LIMIT) {
    const oldest = cache.keys().next().value;
    if (oldest !== undefined) cache.delete(oldest);
  }
  cache.set(text, rows);
  return rows;
}
